import React, { useEffect, useState } from "react";
import { animateScroll as scroll } from "react-scroll";
import BackgroundReel from "./backgroundReel/backgroundReel";
import Button from "@mui/material/Button";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LanguageIcon from "@mui/icons-material/Language";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import bbcCrest from "../../assets/logos/bbc_crest.png";
import bibleReadingPhoto from "../../assets/images/BibleReading.jpg";
import {
  aboutBethelString,
  aboutPath,
  BBC_SLOGAN,
  getDirectionsString,
  homeMissionTeaserString,
  homeServiceTeaserString,
  latestServiceLabelString,
  latestServiceString,
  learnMoreAboutUsString,
  liveNowString,
  morningEveningServicesString,
  ourMissionString,
  romanianAndEnglishString,
  servicePath,
  serviceTimesButtonString,
  serviceTimesPath,
  street,
  city,
  state,
  zip,
  sundayWorshipString,
  translationAvailableString,
  upcomingThisWeekString,
  viewFullScheduleString,
  visitUsString,
  watchServiceString,
  welcomeToString,
  worshipTogetherString,
} from "../../constants";
import { getUrl } from "../../helpers/api/api";
import { liveStatus_endpoint, pcoEvents_endpoint } from "../../helpers/api/apiConstants";
import { momentFromIso } from "../../helpers/dateHelper";
import { getUpcomingServices, WEEKLY_SCHEDULE } from "../../helpers/upcomingServices";
import UrgentMessageDisplay from "../../components/UrgentMessageDisplay";
import LiveVideo from "../../components/liveVideo";
import { useTheme } from "@mui/material";
import { NavLink } from "react-router-dom";
import { useAppSelector } from "../../app/hooks";
import { selectLanguage } from "../../app/slices/languageSlice";

const ADDRESS_MAPS_URL = "https://maps.app.goo.gl/eGUZqK5ZFA1fdj4NA";
const SERVICE_TIMES_ANCHOR = "homeServiceTimesBar";

// Matches serviceTimes.js's time styling exactly - one font for the whole
// "10:00 AM" string, not hour and AM/PM split across two different font
// families/sizes. The split version (secondaryFontFamily numerals forced
// to lining-nums, right up against a tiny sans-serif suffix) read as
// mismatched/uneven; a single Cormorant Garamond string reads as clean
// and unified since it uses that font's own AM/PM glyphs, not a
// bolted-on label.
const TimeDisplay = ({ theme, hour, suffix }) => (
  <span style={{ fontFamily: theme.typography.secondaryFontFamily, fontSize: "20px", fontWeight: 600 }}>
    {hour} {suffix}
  </span>
);

const InfoBox = ({ icon, label, children, theme }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "16px",
      padding: "22px 24px",
      flex: "1 1 260px",
      minWidth: "260px",
      backgroundColor: theme.palette.brand.linen,
      borderRadius: "10px",
      boxShadow: "0 10px 28px rgba(22, 50, 74, 0.12)",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "48px",
        height: "48px",
        minWidth: "48px",
        borderRadius: "50%",
        backgroundColor: theme.palette.brand.mist,
        color: theme.palette.primary.dark,
      }}
    >
      {icon}
    </div>
    <div>
      <div
        style={{
          fontFamily: theme.typography.fontFamily,
          fontSize: "12px",
          fontWeight: 700,
          letterSpacing: "0.08em",
          color: theme.palette.text.secondary,
          marginBottom: "4px",
        }}
      >
        {label}
      </div>
      {children}
    </div>
  </div>
);

const CardLabel = ({ children, theme }) => (
  <div style={{ marginBottom: "12px" }}>
    <div
      style={{
        fontFamily: theme.typography.fontFamily,
        fontSize: "13px",
        fontWeight: 700,
        letterSpacing: "0.08em",
        color: theme.palette.text.secondary,
        marginBottom: "6px",
      }}
    >
      {children}
    </div>
    <div
      style={{
        width: "36px",
        height: "3px",
        backgroundColor: theme.palette.brand.brass,
      }}
    />
  </div>
);

const Home = () => {
  const language = useAppSelector(selectLanguage);
  const theme = useTheme();
  const [statusKnown, setStatusKnown] = useState(false);
  const [live, setLive] = useState(false);
  const [, setLiveOn] = useState("");
  const [liveLink, setLiveLink] = useState("");
  const [liveFeedOpen, setLiveFeedOpen] = useState(false);
  const [scheduleHovered, setScheduleHovered] = useState(false);
  // Starts with the recurring weekly schedule so the section always has
  // something to show, then gets replaced by real Planning Center events
  // below if that fetch succeeds. Keeping this as the initial value also
  // means we fall back to it automatically if Planning Center is down.
  const [upcomingServices, setUpcomingServices] = useState(() => getUpcomingServices(3));

  useEffect(() => {
    fetch(getUrl(pcoEvents_endpoint))
      .then((response) => (response.ok ? response.json() : []))
      .then((data) => {
        if (!Array.isArray(data) || data.length === 0) return;

        // TODO: once staff reliably mark services "Published" in Planning
        // Center, filter this to `.filter((event) => event.published)` so
        // draft/test events staff are still setting up don't show publicly.
        const events = data
          .filter((event) => event.startsAt)
          .map((event) => {
            const date = momentFromIso(event.startsAt);
            return { title: event.title, date, displayTime: date.format("h:mm A") };
          })
          .slice(0, 3);

        if (events.length > 0) {
          setUpcomingServices(events);
        }
      })
      .catch(() => {
        // Planning Center unreachable - keep the recurring-schedule fallback.
      });
  }, []);

  useEffect(() => {
    if (!statusKnown) {
      fetch(getUrl(liveStatus_endpoint))
        .then((response) => {
          if (response.ok) {
            return response.json();
          }
          throw response;
        })
        .then((data) => {
          setStatusKnown(true);
          setLive(data.IS_LIVE);
          setLiveOn(data.LIVE_ON);
          setLiveLink(data.LINK);
        })
        .catch((error) => {
          console.error("Error setting live status. Error: ", error);
        });
    }
  }, [statusKnown]);

  const handleLiveFeedVideoClose = () => {
    setLiveFeedOpen(false);
  };

  const handleButtonPress = () => {
    if (!live) {
      window.location.href = servicePath;
    } else {
      if (liveLink.includes("youtube") && !liveLink.includes("embed")) {
        window.open(liveLink, "_blank");
      } else {
        setLiveFeedOpen(true);
      }
    }
  };

  const scrollToServiceTimes = () => {
    const headerHeight = 72; // matches --nav-height in index.css (desktop value)
    const element = document.getElementById(SERVICE_TIMES_ANCHOR);
    if (element) {
      scroll.scrollTo(element.offsetTop - headerHeight);
    }
  };

  return (
    <div style={{ backgroundColor: theme.palette.background.default, paddingTop: "var(--nav-height)" }}>
      <UrgentMessageDisplay page="Home" />

      {/* HERO */}
      <div
        style={{
          position: "relative",
          height: "min(640px, 78vh)",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
        }}
      >
        <BackgroundReel />
        <div
          style={{
            position: "relative",
            zIndex: "3",
            width: "100%",
            padding: "0 8vw",
            boxSizing: "border-box",
            color: theme.palette.background.default,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "8px",
            }}
          >
            <span style={{ width: "24px", height: "1px", backgroundColor: theme.palette.brand.brass }} />
            <span
              style={{
                fontFamily: theme.typography.fontFamily,
                fontSize: "13px",
                fontWeight: 600,
                letterSpacing: "0.15em",
                color: theme.palette.brand.brass,
                textTransform: "uppercase",
              }}
            >
              {welcomeToString(language)}
            </span>
            <span style={{ width: "24px", height: "1px", backgroundColor: theme.palette.brand.brass }} />
          </div>
          <h1
            style={{
              fontFamily: theme.typography.secondaryFontFamily,
              fontWeight: 600,
              fontSize: "min(9vw, 64px)",
              margin: "0 0 12px",
            }}
          >
            Bethel Bible Church
          </h1>
          <div
            style={{
              fontFamily: theme.typography.paragraphFontFamily,
              fontSize: "min(3.4vw, 20px)",
              fontWeight: 400,
              marginBottom: "28px",
            }}
          >
            {BBC_SLOGAN}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "16px" }}>
            <Button
              variant="contained"
              onClick={handleButtonPress}
              startIcon={<PlayCircleOutlineIcon />}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.background.default,
                fontFamily: theme.typography.fontFamily,
                fontWeight: 600,
                textTransform: "none",
                padding: "10px 22px",
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
              }}
            >
              {live ? liveNowString(language) : latestServiceString(language)}
            </Button>
            <Button
              variant="outlined"
              onClick={scrollToServiceTimes}
              sx={{
                borderColor: theme.palette.brand.brass,
                borderWidth: "1.5px",
                color: theme.palette.background.default,
                fontFamily: theme.typography.fontFamily,
                fontWeight: 600,
                textTransform: "none",
                padding: "10px 22px",
                "&:hover": {
                  borderColor: theme.palette.brand.brass,
                  borderWidth: "1.5px",
                  backgroundColor: "rgba(176, 138, 86, 0.15)",
                },
              }}
            >
              {serviceTimesButtonString(language)}
            </Button>
          </div>
        </div>
      </div>

      {/* THREE INFO BOXES - individual cards, overlaps the bottom of the hero */}
      <div
        id={SERVICE_TIMES_ANCHOR}
        style={{
          maxWidth: "1100px",
          margin: "-48px auto 0",
          position: "relative",
          zIndex: "4",
          padding: "0 24px",
          boxSizing: "border-box",
          display: "flex",
          flexWrap: "wrap",
          gap: "16px",
        }}
      >
        <InfoBox icon={<AccessTimeIcon />} label={sundayWorshipString(language).toUpperCase()} theme={theme}>
          <div style={{ color: theme.palette.text.primary, lineHeight: 1.15 }}>
            <TimeDisplay theme={theme} hour="10:00" suffix="AM" />
            <span style={{ color: theme.palette.brand.brass, fontSize: "26px", fontWeight: 700, margin: "0 8px", verticalAlign: "middle" }}>·</span>
            <TimeDisplay theme={theme} hour="6:00" suffix="PM" />
          </div>
          <div style={{ fontFamily: theme.typography.paragraphFontFamily, fontSize: "13px", color: theme.palette.text.secondary }}>
            {morningEveningServicesString(language)}
          </div>
        </InfoBox>
        <InfoBox icon={<LocationOnIcon />} label={visitUsString(language).toUpperCase()} theme={theme}>
          <div style={{ fontFamily: theme.typography.paragraphFontFamily, fontSize: "15px", color: theme.palette.text.primary }}>
            {street}, {city}, {state} {zip}
          </div>
          <a
            href={ADDRESS_MAPS_URL}
            target="_blank"
            rel="noreferrer"
            style={{ fontFamily: theme.typography.fontFamily, fontSize: "13px", fontWeight: 600, color: theme.palette.primary.main, textDecoration: "none" }}
          >
            {getDirectionsString(language)} →
          </a>
        </InfoBox>
        <InfoBox icon={<LanguageIcon />} label={worshipTogetherString(language).toUpperCase()} theme={theme}>
          <div style={{ fontFamily: theme.typography.secondaryFontFamily, fontSize: "20px", fontWeight: 600, color: theme.palette.text.primary }}>
            {romanianAndEnglishString(language)}
          </div>
          <div style={{ fontFamily: theme.typography.paragraphFontFamily, fontSize: "13px", color: theme.palette.text.secondary }}>
            {translationAvailableString(language)}
          </div>
        </InfoBox>
      </div>

      {/* THREE COLUMN CONTENT */}
      <div
        style={{
          maxWidth: "1100px",
          margin: "0 auto",
          padding: "56px 24px 64px",
          boxSizing: "border-box",
          display: "flex",
          flexWrap: "wrap",
          gap: "32px",
        }}
      >
        {/* LATEST SERVICE */}
        <div style={{ flex: "1 1 340px", minWidth: "300px" }}>
          <div
            style={{
              position: "relative",
              overflow: "hidden",
              borderRadius: "8px",
              minHeight: "320px",
              display: "flex",
              alignItems: "center",
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <div
              role="img"
              aria-label=""
              style={{
                position: "absolute",
                inset: 0,
                backgroundImage: `url(${bibleReadingPhoto})`,
                backgroundRepeat: "no-repeat",
                backgroundSize: "220% auto",
                backgroundPosition: "10% 90%",
              }}
            />
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: `linear-gradient(to right, ${theme.palette.background.paper} 0%, ${theme.palette.background.paper} 40%, transparent 82%)`,
              }}
            />
            <div style={{ position: "relative", zIndex: "1", padding: "32px 28px", maxWidth: "62%" }}>
              <CardLabel theme={theme}>{latestServiceLabelString(language)}</CardLabel>
              <h3
                style={{
                  fontFamily: theme.typography.secondaryFontFamily,
                  fontSize: "25px",
                  fontWeight: 600,
                  color: theme.palette.primary.dark,
                  margin: "0 0 18px",
                  lineHeight: 1.3,
                }}
              >
                {homeServiceTeaserString(language)}
              </h3>
              <div style={{ width: "36px", height: "3px", backgroundColor: theme.palette.brand.brass, marginBottom: "20px" }} />
              <Button
                component={NavLink}
                to={servicePath}
                variant="outlined"
                startIcon={<PlayCircleOutlineIcon />}
                sx={{
                  borderColor: theme.palette.primary.dark,
                  color: theme.palette.primary.dark,
                  fontFamily: theme.typography.fontFamily,
                  fontWeight: 600,
                  textTransform: "none",
                  backgroundColor: theme.palette.background.paper,
                }}
              >
                {watchServiceString(language)}
              </Button>
            </div>
          </div>
        </div>

        {/* ABOUT BETHEL */}
        <div style={{ flex: "1 1 300px", minWidth: "260px" }}>
          <CardLabel theme={theme}>{aboutBethelString(language)}</CardLabel>
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <div style={{ flex: 1 }}>
              <h2
                style={{
                  fontFamily: theme.typography.secondaryFontFamily,
                  fontSize: "28px",
                  fontWeight: 600,
                  margin: "0 0 12px",
                  color: theme.palette.text.primary,
                }}
              >
                {ourMissionString(language)}
              </h2>
              <p
                style={{
                  fontFamily: theme.typography.paragraphFontFamily,
                  fontSize: "15px",
                  color: theme.palette.text.secondary,
                  marginTop: 0,
                }}
              >
                {homeMissionTeaserString(language)}
              </p>
            </div>
            <img
              src={bbcCrest}
              alt=""
              style={{ width: "64px", height: "auto", flexShrink: 0 }}
            />
          </div>
          <NavLink
            to={aboutPath}
            style={{
              fontFamily: theme.typography.fontFamily,
              fontSize: "14px",
              fontWeight: 600,
              color: theme.palette.primary.main,
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            {learnMoreAboutUsString(language)} <ArrowForwardIcon sx={{ fontSize: "16px" }} />
          </NavLink>
        </div>

        {/* UPCOMING THIS WEEK */}
        <div style={{ flex: "1 1 300px", minWidth: "260px" }}>
          <CardLabel theme={theme}>{upcomingThisWeekString(language)}</CardLabel>
          <div>
            {upcomingServices.map((service, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "14px",
                  padding: "10px 0",
                  borderBottom: index < upcomingServices.length - 1 ? `1px solid ${theme.palette.brand.border}` : "none",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "44px",
                    height: "44px",
                    minWidth: "44px",
                    borderRadius: "8px",
                    backgroundColor: theme.palette.background.default,
                    border: `1.5px solid ${theme.palette.brand.brass}`,
                  }}
                >
                  <span style={{ fontFamily: theme.typography.fontFamily, fontSize: "10px", fontWeight: 700, color: theme.palette.brand.brass }}>
                    {service.date.format("MMM").toUpperCase()}
                  </span>
                  <span style={{ fontFamily: theme.typography.fontFamily, fontSize: "16px", fontWeight: 700, color: theme.palette.primary.dark }}>
                    {service.date.format("D")}
                  </span>
                </div>
                <div>
                  <div style={{ fontFamily: theme.typography.paragraphFontFamily, fontSize: "14px", fontWeight: 500, color: theme.palette.text.primary }}>
                    {service.title ?? service.nameString(language)}
                  </div>
                  <div style={{ fontFamily: theme.typography.paragraphFontFamily, fontSize: "13px", color: theme.palette.text.secondary }}>
                    {service.displayTime}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div
            style={{ position: "relative", marginTop: "12px", display: "inline-block" }}
            onMouseEnter={() => setScheduleHovered(true)}
            onMouseLeave={() => setScheduleHovered(false)}
          >
            <NavLink
              to={serviceTimesPath}
              style={{
                fontFamily: theme.typography.fontFamily,
                fontSize: "14px",
                fontWeight: 600,
                color: theme.palette.primary.main,
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              {viewFullScheduleString(language)} <ArrowForwardIcon sx={{ fontSize: "16px" }} />
            </NavLink>
            {scheduleHovered && (
              <div
                style={{
                  position: "absolute",
                  bottom: "calc(100% + 8px)",
                  left: 0,
                  zIndex: "5",
                  minWidth: "260px",
                  backgroundColor: theme.palette.background.default,
                  border: `1px solid ${theme.palette.brand.border}`,
                  borderRadius: "8px",
                  boxShadow: "0 8px 24px rgba(22, 50, 74, 0.2)",
                  padding: "14px 16px",
                }}
              >
                {WEEKLY_SCHEDULE.map((service, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: "12px",
                      padding: "6px 0",
                      fontFamily: theme.typography.paragraphFontFamily,
                      fontSize: "13px",
                      color: theme.palette.text.primary,
                    }}
                  >
                    <span>{service.nameString(language)}</span>
                    <span style={{ color: theme.palette.text.secondary, whiteSpace: "nowrap" }}>{service.displayTime}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <LiveVideo
        videoLink={liveLink}
        open={liveFeedOpen}
        onClose={handleLiveFeedVideoClose}
      />
    </div>
  );
};

export default Home;
